<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.order.actions.addReservations.title')"
    :info="$t('components.order.actions.addReservations.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.addReservations.title'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          :info="$t('components.order.actions.addReservations.customerInfo')"
          class="mb-4 block"
          rules="required"
        >
          <customer-select v-model="customer" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="customer"
          v-slot="{ status }"
          :label="$t('general.appointment')"
          :name="$t('general.appointment')"
          class="mb-4 block"
          rules="required"
        >
          <div class="block border rounded-lg h-80 bg-gray-50 dark:bg-gray-900 dark:border-gray-800 overflow-hidden">
            <div v-if="loadingReservations" class="flex h-full items-center justify-center gap-3 text-sm opacity-70" role="status">
              <o-loader size="20px" />
              <span>{{ $t('components.order.actions.addReservations.findingAppointments') }}</span>
            </div>
            <checkbox-group-field
              v-else-if="reservationOptions.length"
              v-model="selected"
              :options="reservationOptions"
              :status="status"
              search
              class="h-80"
            >
              <template #option="{ option }">
                <div class="w-full min-w-0 ml-3 flex items-start justify-between gap-2 text-gray-800 dark:text-gray-100 pt-1">
                  <p class="min-w-0 leading-snug">
                    {{ option.text }}
                  </p>
                  <o-chip v-if="option.value.isStatusCancelled" size="xs" variant="danger" flat class="flex-shrink-0">
                    {{ $t('general.cancelled') }}
                  </o-chip>
                </div>
              </template>
            </checkbox-group-field>
            <p v-else class="text-sm opacity-70 text-center p-5">
              {{ $t('components.order.actions.addReservations.noAppointments') }}
            </p>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { Customer } from '~/_api/models/customer'

import CustomerSelect from '@/components/customer/elements/Select.vue'
import {
  CheckboxGroupField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const dialog = ref<any>()
const form = ref<any>()

const customer = ref<any>(new Customer({ ...props.order.customer }))
const selected = ref<any[]>([])
const reservations = ref<any>(null)
const loadingReservations = ref(false)
let latestReservationsRequest = 0

const reservationOptions = computed(() => {
  const reservationsVal = reservations.value

  if (!reservationsVal) { return [] }

  return reservationsVal
    .filter((reservation: any) => !reservation.isPaid)
    .map((reservation: any) => ({
      value: reservation,
      text: reservation.infoLabel,
    }))
})

watch(customer, () => {
  fetchReservations()
})

function open () {
  fetchReservations()
  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const reservationsVal = selected.value
    const reservationIds = reservationsVal
      .filter((reservation: any) => reservation.isSingle)
      .map((reservation: any) => String(reservation.idReservation))

    const reservationCustomerIds = reservationsVal
      .filter((reservation: any) => reservation.isGroup)
      .map((reservation: any) => reservation.reservationCustomer?.idReservationCustomer)
      .filter(Boolean)
      .map((idReservationCustomer: any) => String(idReservationCustomer))

    const response = await $api.order.addReservations(String(props.order.idOrder), {
      reservationIds,
      reservationCustomerIds,
    })
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addReservations.success'),
      text: t('components.order.actions.addReservations.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return form.value?.validate()
}

async function fetchReservations () {
  const requestId = ++latestReservationsRequest
  selected.value = []

  const customerVal = customer.value

  if (customerVal) {
    loadingReservations.value = true

    try {
      const response = await $api.customer.unpaidReservations(customerVal.objectID())

      if (requestId !== latestReservationsRequest) return

      reservations.value = response.items
    } catch (e) {
      if (requestId !== latestReservationsRequest) return

      console.log(e)
      reservations.value = null
    } finally {
      if (requestId === latestReservationsRequest) {
        loadingReservations.value = false
      }
    }
  } else {
    reservations.value = null
    loadingReservations.value = false
  }
}

defineExpose({ open })
</script>
